import { useEffect, useState, useSyncExternalStore } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import {
  canRedo,
  canUndo,
  commit,
  createUndoTree,
  currentNode,
  jumpTo,
  redo,
  undo,
  trimUndoTree,
  type UndoStep,
  type UndoTree,
} from './undoTree';

export interface UndoTreeControls<S> {
  tree: UndoTree<S>;
  /** Records a state the editor has just applied itself. */
  record: (state: S, step: Omit<UndoStep<S>, 'at'>) => void;
  undo: () => void;
  redo: () => void;
  jumpTo: (seq: number) => void;
  canUndo: boolean;
  canRedo: boolean;
  reset: (state: S, restored?: UndoTree<S> | null) => void;
}

/**
 * An editor's undo tree over `value` (see `./undoTree.ts`). The editor applies
 * its own edits and `record`s them; undo, redo and jumps move the tree and
 * hand the node's state to `apply`. A `value` that changes any other way — an
 * agent's edit, a restore — is recorded as a step labelled `elsewhereLabel`,
 * so undo never skips back over it.
 *
 * Holds the history for as long as the editor is mounted; nothing is stored.
 */
export function useUndoTree<S>({
  value,
  apply,
  equals,
  openedLabel,
  elsewhereLabel,
  restored = null,
  now = Date.now,
  describeChange,
  maxNodes,
  enabled = true,
  sessionKey,
  capture,
}: {
  value: S;
  apply: (state: S) => void;
  equals: (a: S, b: S) => boolean;
  /** The root's label: the state the editor opened on. */
  openedLabel: string;
  elsewhereLabel: string;
  /** A history kept from before (a reload), whose current state is `value`. */
  restored?: UndoTree<S> | null;
  now?: () => number;
  /** Describes a pending local edit; otherwise uses elsewhereLabel. */
  describeChange?: (base: S, next: S) => Omit<UndoStep<S>, 'at'> | undefined;
  maxNodes?: number;
  enabled?: boolean;
  /** Opening another editor session starts its own history. */
  sessionKey?: string;
  capture?: (state: S) => S;
}): UndoTreeControls<S> {
  const snapshot = (state: S) => (capture ? capture(state) : state);
  const fresh = () =>
    restored ??
    createUndoTree(snapshot(value), { at: now(), label: openedLabel });
  const [session, setSession] = useState(() => ({
    key: sessionKey,
    tree: fresh(),
  }));
  const bound = (tree: UndoTree<S>) =>
    maxNodes === undefined ? tree : trimUndoTree(tree, maxNodes);
  let shown = session.tree;
  if (session.key !== sessionKey) {
    shown = bound(fresh());
    setSession({ key: sessionKey, tree: shown });
  }
  if (enabled && !equals(currentNode(shown).state, value)) {
    const step = describeChange?.(currentNode(shown).state, value) ?? {
      label: elsewhereLabel,
    };
    shown = bound(
      commit(shown, snapshot(value), { ...step, at: now() }, equals),
    );
    setSession({ key: sessionKey, tree: shown });
  }
  const move = (next: UndoTree<S>) => {
    if (!enabled || next === shown) return;
    setSession({ key: sessionKey, tree: next });
    apply(currentNode(next).state);
  };
  return {
    tree: shown,
    record: (state, step) => {
      const at = now();
      setSession((latest) => ({
        ...latest,
        tree: equals(currentNode(latest.tree).state, state)
          ? latest.tree
          : bound(
              commit(latest.tree, snapshot(state), { ...step, at }, equals),
            ),
      }));
    },
    undo: () => move(undo(shown)),
    redo: () => move(redo(shown)),
    jumpTo: (seq) => move(jumpTo(shown, seq)),
    canUndo: enabled && canUndo(shown),
    canRedo: enabled && canRedo(shown),
    reset: (state, kept = null) =>
      setSession({
        key: sessionKey,
        tree: bound(
          kept ??
            createUndoTree(snapshot(state), { at: now(), label: openedLabel }),
        ),
      }),
  };
}

// The editor surfaces whose undo keys are on, oldest first. Only the last one
// answers them: a Report laid out inside a Workbook takes the keys from the
// Workbook while it is being laid out, and hands them back when it stops.
let owners: readonly symbol[] = [];
const ownerListeners = new Set<() => void>();
function setOwners(next: readonly symbol[]) {
  owners = next;
  for (const listener of ownerListeners) listener();
}
function subscribeOwners(listener: () => void) {
  ownerListeners.add(listener);
  return () => ownerListeners.delete(listener);
}

/** Whether this surface, enabled, is the one the undo keys belong to. */
function useUndoHotkeysOwner(enabled: boolean): boolean {
  const [token] = useState(() => Symbol('undo surface'));
  useEffect(() => {
    if (!enabled) return;
    setOwners([...owners, token]);
    return () => setOwners(owners.filter((owner) => owner !== token));
  }, [enabled, token]);
  const owner = useSyncExternalStore(subscribeOwners, () => owners.at(-1));
  return enabled && owner === token;
}

/**
 * Cmd/Ctrl+Z undoes; Cmd/Ctrl+Shift+Z and Ctrl+Y redo. Keys typed in a text
 * field or a rich editor are left to the field's own undo, as a spreadsheet
 * leaves them to the cell being edited. Of several enabled surfaces, the one
 * enabled last answers the keys.
 */
export function useUndoHotkeys(
  controls: Pick<UndoTreeControls<unknown>, 'undo' | 'redo'>,
  enabled: boolean,
) {
  const options = {
    enabled: useUndoHotkeysOwner(enabled),
    preventDefault: true,
  };
  // Cmd and Ctrl alike rather than `mod`, which trusts the user agent: a
  // browser reporting another platform (device emulation, a spoofed agent)
  // would otherwise leave Cmd+Z to the page's own undo.
  useHotkeys(['meta+z', 'ctrl+z'], () => controls.undo(), options, [controls]);
  useHotkeys(
    ['meta+shift+z', 'ctrl+shift+z', 'ctrl+y'],
    () => controls.redo(),
    options,
    [controls],
  );
}
